import { useEffect, useState } from "react";
import { fetchRules, saveRules } from "../api/client";
import { Alert } from "../components/Alert";
import { Spinner } from "../components/Spinner";
import { useAsyncData } from "../hooks/useAsyncData";
import { formatError } from "../utils/formatError";
import { MAX_RESULT_LIMIT } from "../api/types";
import type { TuningRules } from "../api/types";

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "never";
}

/**
 * Why the pair cannot be saved, or null when it can.
 *
 * Checked here as well as on the server so the admin is told before the round trip, not by a 422.
 * The server remains the authority — this is a courtesy, never the thing enforcing the rule.
 */
function limitProblem(resultLimit: number, resultsShown: number): string | null {
  if (!Number.isInteger(resultLimit) || resultLimit < 1 || resultLimit > MAX_RESULT_LIMIT) {
    return `Results per search must be a whole number between 1 and ${MAX_RESULT_LIMIT}.`;
  }
  if (!Number.isInteger(resultsShown) || resultsShown < 1) {
    return "Results shown must be a whole number of at least 1.";
  }
  if (resultsShown > resultLimit) {
    return "Results shown cannot exceed results per search — “View all” would promise rows that "
      + "were never retrieved.";
  }
  return null;
}

export function RulesScreen(): JSX.Element {
  const { data: rules, loading, error, reload } = useAsyncData(() => fetchRules(), []);
  const [globs, setGlobs] = useState<string[]>([]);
  const [newGlob, setNewGlob] = useState("");
  const [augmentWithNativeSearch, setAugmentWithNativeSearch] = useState(false);
  const [summaryEnabled, setSummaryEnabled] = useState(true);
  // Held as strings so a half-typed value ("" while retyping) survives without becoming NaN.
  const [resultLimit, setResultLimit] = useState("20");
  const [resultsShown, setResultsShown] = useState("5");
  const [saved, setSaved] = useState<TuningRules | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed local editable state whenever a fresh copy loads from the server.
  useEffect(() => {
    if (rules) {
      setGlobs(rules.exclusion_globs);
      setAugmentWithNativeSearch(rules.augment_with_native_search);
      setSummaryEnabled(rules.summary_enabled);
      setResultLimit(String(rules.result_limit));
      setResultsShown(String(rules.results_shown));
      setSaved(rules);
    }
  }, [rules]);

  const handleAddGlob = (): void => {
    const value = newGlob.trim();
    if (value && !globs.includes(value)) {
      setGlobs([...globs, value]);
      setNewGlob("");
    }
  };

  const handleRemoveGlob = (glob: string): void => {
    setGlobs(globs.filter((existing) => existing !== glob));
  };

  const limit = Number(resultLimit);
  const shown = Number(resultsShown);
  const problem = limitProblem(limit, shown);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      const result = await saveRules({
        exclusion_globs: globs,
        augment_with_native_search: augmentWithNativeSearch,
        summary_enabled: summaryEnabled,
        result_limit: limit,
        results_shown: shown,
        updated_by: "",
        updated_at: null,
      });
      setSaved(result);
      setGlobs(result.exclusion_globs);
      setResultLimit(String(result.result_limit));
      setResultsShown(String(result.results_shown));
    } catch (saveErr) {
      setSaveError(formatError(saveErr));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner label="Loading rules…" />;
  }

  if (error) {
    return (
      <div className="rules-screen">
        <Alert variant="error">Could not load rules: {formatError(error)}</Alert>
        <button type="button" className="button button--secondary" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="rules-screen">
      <section>
        <h2>Exclusion patterns</h2>
        <p>
          Files matching any of these are excluded from indexing entirely, and are removed on the
          next run if they are already indexed. A pattern with no wildcard is matched as a whole
          word against the filename, however it is spaced &mdash; <code>do not use</code> also
          catches <code>Deck_DO_NOT_USE.pptx</code>. A pattern with a wildcard is matched as a
          glob, so <code>*old*</code> reaches inside words and would also hide{" "}
          <code>Goldman Holdings.xlsx</code>.
        </p>
        <ul className="glob-list">
          {globs.map((glob) => (
            <li key={glob}>
              <code>{glob}</code>
              <button
                type="button"
                className="button button--secondary button--small"
                onClick={() => handleRemoveGlob(glob)}
                aria-label={`Remove ${glob}`}
              >
                Remove
              </button>
            </li>
          ))}
          {globs.length === 0 && (
            <li className="glob-list__empty">No exclusion patterns configured.</li>
          )}
        </ul>
        <div className="glob-add">
          <input
            type="text"
            value={newGlob}
            onChange={(event) => setNewGlob(event.target.value)}
            placeholder="e.g. do not use, or **/*.tmp"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAddGlob();
              }
            }}
          />
          <button type="button" className="button button--secondary" onClick={handleAddGlob}>
            Add
          </button>
        </div>
      </section>

      <section>
        <h2>Search behaviour</h2>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={augmentWithNativeSearch}
            onChange={(event) => setAugmentWithNativeSearch(event.target.checked)}
          />
          Augment with native search
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={summaryEnabled}
            onChange={(event) => setSummaryEnabled(event.target.checked)}
          />
          Generate file summaries
        </label>
      </section>

      <section>
        <h2>Results</h2>
        <p>
          How many files a search returns, and how many the chat shows before offering the rest on
          their own page.
        </p>
        <label className="field-row">
          <span className="field-row__label">Results per search</span>
          <input
            type="number"
            min={1}
            max={MAX_RESULT_LIMIT}
            value={resultLimit}
            onChange={(event) => setResultLimit(event.target.value)}
          />
          <span className="field-row__hint">
            The most any one search may return. Enforced on the server, so it caps what an MCP
            client asks for too.
          </span>
        </label>
        <label className="field-row">
          <span className="field-row__label">Results shown before “View all”</span>
          <input
            type="number"
            min={1}
            max={MAX_RESULT_LIMIT}
            value={resultsShown}
            onChange={(event) => setResultsShown(event.target.value)}
          />
          <span className="field-row__hint">
            Cards drawn under an answer. The remainder stay one click away.
          </span>
        </label>
        {problem && <Alert variant="warning">{problem}</Alert>}
      </section>

      {saveError && <Alert variant="error">Could not save rules: {saveError}</Alert>}

      <div className="rules-screen__actions">
        <button
          type="button"
          className="button button--primary"
          onClick={handleSave}
          disabled={saving || problem !== null}
        >
          {saving ? "Saving…" : "Save rules"}
        </button>
        {saved && (
          <span className="rules-screen__meta">
            Last saved by {saved.updated_by || "unknown"} at {formatDate(saved.updated_at)}
          </span>
        )}
      </div>
    </div>
  );
}
